
import React, { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FeedbackTicket } from '../types';

interface TicketDetailDialogProps {
  ticket: FeedbackTicket | null;
  onOpenChange: (open: boolean) => void;
  onStatusChange: (id: string, status: 'Open' | 'In Progress' | 'Resolved') => Promise<boolean>;
  refreshTickets: () => void;
}

export const TicketDetailDialog: React.FC<TicketDetailDialogProps> = ({
  ticket,
  onOpenChange,
  onStatusChange,
  refreshTickets,
}) => {
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [currentStatus, setCurrentStatus] = useState<'Open' | 'In Progress' | 'Resolved'>('Open');
  
  useEffect(() => {
    if (ticket) {
      setCurrentStatus(ticket.status as 'Open' | 'In Progress' | 'Resolved');
    }
  }, [ticket]);

  if (!ticket) return null;

  const handleStatusChange = async (value: string) => {
    if (!ticket) return;
    
    setUpdatingStatus(true);
    try {
      const status = value as 'Open' | 'In Progress' | 'Resolved';
      const success = await onStatusChange(ticket.id, status);
      
      if (success) {
        setCurrentStatus(status);
        refreshTickets();
      }
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={!!ticket} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Feedback Ticket</DialogTitle>
          <DialogDescription>
            Submitted on {new Date(ticket.created_at).toLocaleString()}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <span className="font-medium">Status:</span>
            <div className="col-span-3">
              <Select 
                value={currentStatus}
                onValueChange={handleStatusChange}
                disabled={updatingStatus}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Open">Open</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Resolved">Resolved</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <span className="font-medium">Category:</span>
            <div className="col-span-3">
              <Badge variant={
                ticket.category === 'Bug' ? "destructive" :
                ticket.category === 'Feature Request' ? "default" : "secondary"
              }>
                {ticket.category}
              </Badge>
            </div>
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <span className="font-medium">User:</span>
            <div className="col-span-3">
              {ticket.user_info?.full_name || 'Unknown'}
            </div>
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <span className="font-medium">Email:</span>
            <div className="col-span-3">
              {ticket.user_info?.email || 'No email'}
            </div>
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <span className="font-medium">Source Page:</span>
            <div className="col-span-3">
              {ticket.page_source || 'Unknown'}
            </div>
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <span className="font-medium">Contact Preference:</span>
            <div className="col-span-3">
              {ticket.contact_preference}
            </div>
          </div>
          {ticket.phone_number && (
            <div className="grid grid-cols-4 items-center gap-4">
              <span className="font-medium">Phone Number:</span>
              <div className="col-span-3">
                {ticket.phone_number}
              </div>
            </div>
          )}
          <div className="grid grid-cols-4 items-center gap-4">
            <span className="font-medium">Urgency:</span>
            <div className="col-span-3">
              <Badge variant={
                ticket.urgency === 'Critical' ? "destructive" :
                ticket.urgency === 'High' ? "destructive" :
                ticket.urgency === 'Medium' ? "default" : "secondary"
              }>
                {ticket.urgency}
              </Badge>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-4">
            <span className="font-medium">Description:</span>
            <div className="col-span-3 whitespace-pre-wrap border rounded-md p-2">
              {ticket.description}
            </div>
          </div>
          {ticket.steps && (
            <div className="grid grid-cols-4 gap-4">
              <span className="font-medium">Steps to Reproduce:</span>
              <div className="col-span-3 whitespace-pre-wrap border rounded-md p-2">
                {ticket.steps}
              </div>
            </div>
          )}
          {ticket.additional_comments && (
            <div className="grid grid-cols-4 gap-4">
              <span className="font-medium">Additional Comments:</span>
              <div className="col-span-3 whitespace-pre-wrap border rounded-md p-2">
                {ticket.additional_comments}
              </div>
            </div>
          )}
          {ticket.screenshot_url && (
            <div className="grid grid-cols-4 gap-4">
              <span className="font-medium">Screenshot:</span>
              <div className="col-span-3">
                <img 
                  src={ticket.screenshot_url} 
                  alt="Screenshot" 
                  className="rounded-md max-h-[300px] object-contain border"
                />
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={handleClose} variant="default">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
